/**
 * Wie hoch ein Bild sein wird, bevor es da ist. (05.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin, zum dritten Mal am selben Tag: *„Das Scrollen geht leider immer
 * noch nicht. Der Content springt an gewissen Stellen so eigenartig und
 * stört den Lesefluss, sodass man nicht wirklich die Ziele anpeilen kann."*
 *
 * Zwei Ursachen waren schon behoben — vierzehn Stellen, die bedingungslos
 * ans Ende sprangen, und eine Blase mit Bild, die auf 96 Punkte geschätzt
 * wurde. Die dritte hatte ich dabei ausdrücklich offen gelassen und in den
 * Code geschrieben: *„Die Bilder selbst tragen weder `width`/`height` noch
 * ein Seitenverhältnis, sind also bis zum Laden 0 hoch."*
 *
 * Genau das ist der Rest: `loading="lazy"` lädt beim Heranscrollen nach, und
 * in dem Augenblick wächst das Bild von 0 auf seine echte Höhe. Alles
 * darunter rutscht weg — unter dem Finger, mitten im Lesen.
 *
 * ## Warum kein Server-Umbau
 *
 * Die Maße stehen in den Dateien; `uploads` speichert sie nicht. Sie
 * nachzutragen hieße: zwei Spalten, ein Rückfüllen über 89 vorhandene
 * Bilder, ein neuer Weg durch den Verlauf-Abruf. Das ist der große Weg.
 *
 * Der kleine reicht: Ein Bild, das einmal geladen wurde, hat seine Maße
 * verraten. Wer sie behält, kennt sie beim nächsten Mal — und das nächste
 * Mal ist bei einem Chat-Verlauf die Regel, nicht die Ausnahme.
 *
 * ⚠️ Für ein noch nie gesehenes Bild bleibt ein Rest: Dann gilt der
 *    Standard aus dem CSS. Er ist ein Kompromiss, kein Wissen — aber ein
 *    reservierter Platz von ungefähr richtiger Größe ist ungleich besser
 *    als null.
 */

const SCHLUESSEL = "vespera.bildmass.v1";
/** Genug für einen langen Verlauf, klein genug für den Speicher. */
const HOECHSTENS = 400;

type Masse = Record<string, string>;

let zwischen: Masse | null = null;

function lies(): Masse {
  if (zwischen) return zwischen;
  try {
    const roh = localStorage.getItem(SCHLUESSEL);
    zwischen = roh ? (JSON.parse(roh) as Masse) : {};
  } catch {
    // Privater Modus, volles Kontingent — dann eben ohne Gedächtnis.
    zwischen = {};
  }
  return zwischen;
}

function schreib(m: Masse): void {
  zwischen = m;
  try {
    localStorage.setItem(SCHLUESSEL, JSON.stringify(m));
  } catch {
    /* siehe lies() */
  }
}

/** Das gemerkte Verhältnis („1200 / 900") oder null. */
export function holeSeitenverhaeltnis(url: string): string | null {
  if (!url) return null;
  return lies()[url] ?? null;
}

/**
 * Ein geladenes Bild ausmessen und behalten.
 *
 * ⚠️ Nur echte Maße. Ein Bild, das nicht laden konnte, meldet 0 — das als
 *    Verhältnis zu speichern hieße, den Fehler dauerhaft zu machen.
 */
export function merkeSeitenverhaeltnis(
  url: string,
  breite: number,
  hoehe: number,
): string | null {
  if (!url || !(breite > 0) || !(hoehe > 0)) return null;
  const wert = `${breite} / ${hoehe}`;
  const m = { ...lies() };
  if (m[url] === wert) return wert;
  m[url] = wert;
  // Die ältesten fallen heraus — ein Verlauf wächst, der Speicher nicht.
  const schluessel = Object.keys(m);
  if (schluessel.length > HOECHSTENS) {
    for (const k of schluessel.slice(0, schluessel.length - HOECHSTENS)) {
      delete m[k];
    }
  }
  schreib(m);
  return wert;
}

/**
 * Ein Bild so vorbereiten, dass es seinen Platz kennt.
 *
 * Setzt das gemerkte Verhältnis sofort und trägt das echte nach, sobald das
 * Bild geladen ist. EIN Weg für alle Bilder im Verlauf — sonst hätte die
 * nächste Stelle wieder ihr eigenes Verhalten.
 */
export function ruesteBildAus(img: HTMLImageElement, url: string): void {
  const gemerkt = holeSeitenverhaeltnis(url);
  if (gemerkt) img.style.setProperty("--seitenverhaeltnis", gemerkt);
  img.addEventListener(
    "load",
    () => {
      const wert = merkeSeitenverhaeltnis(
        url,
        img.naturalWidth,
        img.naturalHeight,
      );
      if (wert) img.style.setProperty("--seitenverhaeltnis", wert);
    },
    { once: true },
  );
}

/** Nur Tests. */
export function _leereBildmasse(): void {
  zwischen = null;
  try {
    localStorage.removeItem(SCHLUESSEL);
  } catch {
    /* egal */
  }
}
